import { ref } from "vue";
import { defineStore } from "pinia";
import { describeApiError } from "@/api/client";
import type {
  DismissInboxNotificationResponse,
  InboxNotification,
  InboxNotificationListResponse,
  MarkInboxNotificationReadResponse,
} from "@/api/inbox-notification-schemas";
import { washboxApi } from "@/api/washbox";
import { keycloakSession } from "@/auth/keycloak-session";

export type NotificationInboxStatus = "idle" | "loading" | "ready" | "error";
export type NotificationInboxAction = "read" | "dismiss";

export const NOTIFICATION_INBOX_STALE_AFTER_MS = 60_000;

interface ScheduledAction {
  readonly token: symbol;
  readonly notificationId: string;
  readonly type: NotificationInboxAction;
  readonly epoch: number;
  readonly request: Promise<void>;
}

function preferredNotification(
  current: InboxNotification | undefined,
  incoming: InboxNotification,
): InboxNotification {
  if (current === undefined || incoming.versionNo > current.versionNo) return incoming;
  if (incoming.versionNo < current.versionNo) return current;
  if (current.readAt !== null && incoming.readAt === null) return current;
  return incoming;
}

export const useNotificationInboxStore = defineStore("notification-inbox", () => {
  const status = ref<NotificationInboxStatus>("idle");
  const notifications = ref<InboxNotification[]>([]);
  const unreadCount = ref<number | null>(null);
  const nextCursor = ref<string | null>(null);
  const errorMessage = ref<string | null>(null);
  const loadMoreErrorMessage = ref<string | null>(null);
  const isRefreshing = ref(false);
  const isLoadingMore = ref(false);
  const pendingActions = ref<Record<string, NotificationInboxAction>>({});
  const itemErrors = ref<Record<string, string>>({});
  const lastLoadedAt = ref(0);

  let epoch = 0;
  let listRevision = 0;
  let dataRevision = 0;
  let firstPageLoading: Promise<void> | null = null;
  let nextPageLoading: Promise<void> | null = null;
  let mutationQueue: Promise<void> = Promise.resolve();
  const scheduledActions: ScheduledAction[] = [];
  const notificationTombstones = new Map<string, number>();

  keycloakSession.subscribe((authenticated) => {
    if (!authenticated) reset();
  });

  function canApplyListResult(
    operationEpoch: number,
    operationListRevision: number,
    operationDataRevision: number,
  ): boolean {
    return (
      epoch === operationEpoch &&
      listRevision === operationListRevision &&
      dataRevision === operationDataRevision
    );
  }

  function mergeNotificationPage(
    response: InboxNotificationListResponse,
    append: boolean,
  ): InboxNotification[] {
    const currentById = new Map(
      notifications.value.map((notification) => [notification.id, notification]),
    );
    const merged = append ? [...notifications.value] : [];
    const indexes = new Map(merged.map((notification, index) => [notification.id, index]));

    for (const incoming of response.notifications) {
      if (notificationTombstones.has(incoming.id)) continue;

      const selected = preferredNotification(currentById.get(incoming.id), incoming);
      const existingIndex = indexes.get(incoming.id);
      if (existingIndex === undefined) {
        indexes.set(incoming.id, merged.length);
        merged.push(selected);
      } else {
        merged.splice(existingIndex, 1, selected);
      }
    }
    return merged;
  }

  function applyPage(response: InboxNotificationListResponse, append: boolean): void {
    notifications.value = mergeNotificationPage(response, append);
    nextCursor.value = response.nextCursor;
    unreadCount.value = response.unreadCount;
    lastLoadedAt.value = Date.now();
    status.value = "ready";
    errorMessage.value = null;
    if (append) loadMoreErrorMessage.value = null;
  }

  function load(force = false): Promise<void> {
    if (!force && status.value === "ready") return Promise.resolve();
    if (firstPageLoading !== null) return firstPageLoading;

    const operationEpoch = epoch;
    const operationListRevision = ++listRevision;
    const operationDataRevision = dataRevision;
    nextPageLoading = null;
    isLoadingMore.value = false;
    loadMoreErrorMessage.value = null;
    errorMessage.value = null;
    if (notifications.value.length === 0) status.value = "loading";
    else isRefreshing.value = true;

    const request = washboxApi
      .listInboxNotifications()
      .then((response) => {
        if (!canApplyListResult(operationEpoch, operationListRevision, operationDataRevision)) {
          return;
        }
        applyPage(response, false);
      })
      .catch((error: unknown) => {
        if (!canApplyListResult(operationEpoch, operationListRevision, operationDataRevision)) {
          return;
        }
        errorMessage.value = describeApiError(error);
        status.value = notifications.value.length === 0 ? "error" : "ready";
      })
      .finally(() => {
        if (firstPageLoading === request) firstPageLoading = null;
        if (epoch === operationEpoch && listRevision === operationListRevision) {
          isRefreshing.value = false;
        }
      });
    firstPageLoading = request;
    return request;
  }

  function refreshIfStale(maxAgeMs = NOTIFICATION_INBOX_STALE_AFTER_MS): Promise<void> {
    const boundedMaxAge = Number.isFinite(maxAgeMs) ? Math.max(0, maxAgeMs) : 0;
    if (
      status.value === "ready" &&
      lastLoadedAt.value > 0 &&
      Date.now() - lastLoadedAt.value < boundedMaxAge
    ) {
      return Promise.resolve();
    }
    return load(true);
  }

  function loadMore(): Promise<void> {
    const cursor = nextCursor.value;
    if (cursor === null || isRefreshing.value) return Promise.resolve();
    if (nextPageLoading !== null) return nextPageLoading;

    const operationEpoch = epoch;
    const operationListRevision = listRevision;
    const operationDataRevision = dataRevision;
    isLoadingMore.value = true;
    loadMoreErrorMessage.value = null;

    const request = washboxApi
      .listInboxNotifications(cursor)
      .then((response) => {
        if (!canApplyListResult(operationEpoch, operationListRevision, operationDataRevision)) {
          return;
        }
        applyPage(response, true);
      })
      .catch((error: unknown) => {
        if (!canApplyListResult(operationEpoch, operationListRevision, operationDataRevision)) {
          return;
        }
        loadMoreErrorMessage.value = describeApiError(error);
      })
      .finally(() => {
        if (nextPageLoading === request) nextPageLoading = null;
        if (epoch === operationEpoch && listRevision === operationListRevision) {
          isLoadingMore.value = false;
        }
      });
    nextPageLoading = request;
    return request;
  }

  function setItemError(notificationId: string, message: string | null): void {
    const replacement = { ...itemErrors.value };
    if (message === null) delete replacement[notificationId];
    else replacement[notificationId] = message;
    itemErrors.value = replacement;
  }

  function setPendingAction(notificationId: string, action: NotificationInboxAction | null): void {
    const replacement = { ...pendingActions.value };
    if (action === null) delete replacement[notificationId];
    else replacement[notificationId] = action;
    pendingActions.value = replacement;
  }

  function tombstoneNotification(notificationId: string, versionNo = 0): void {
    const existingVersion = notificationTombstones.get(notificationId) ?? 0;
    notificationTombstones.set(notificationId, Math.max(existingVersion, versionNo));
    notifications.value = notifications.value.filter(({ id }) => id !== notificationId);
  }

  function applyReadResponse(response: MarkInboxNotificationReadResponse): void {
    if (response.status === "unavailable") {
      tombstoneNotification(response.notificationId);
    } else if (!notificationTombstones.has(response.notification.id)) {
      const index = notifications.value.findIndex(({ id }) => id === response.notification.id);
      if (index >= 0) {
        const current = notifications.value[index];
        if (current !== undefined) {
          notifications.value.splice(
            index,
            1,
            preferredNotification(current, response.notification),
          );
        }
      }
    }
    unreadCount.value = response.unreadCount;
  }

  function applyDismissResponse(response: DismissInboxNotificationResponse): void {
    tombstoneNotification(response.notificationId, response.versionNo);
    unreadCount.value = response.unreadCount;
  }

  function actionCanRun(notificationId: string, action: NotificationInboxAction): boolean {
    const notification = notifications.value.find(({ id }) => id === notificationId);
    return notification !== undefined && (action === "dismiss" || notification.readAt === null);
  }

  function syncPendingAction(notificationId: string): void {
    const nextAction = scheduledActions.find(
      (scheduled) => scheduled.epoch === epoch && scheduled.notificationId === notificationId,
    );
    setPendingAction(notificationId, nextAction?.type ?? null);
  }

  function runAction(notificationId: string, action: NotificationInboxAction): Promise<void> {
    const operationEpoch = epoch;
    const duplicate = scheduledActions.find(
      (scheduled) =>
        scheduled.epoch === operationEpoch &&
        scheduled.notificationId === notificationId &&
        scheduled.type === action,
    );
    if (duplicate !== undefined) return duplicate.request;

    setItemError(notificationId, null);
    const actionToken = Symbol();
    const operation = mutationQueue.then(async () => {
      if (epoch !== operationEpoch || !actionCanRun(notificationId, action)) return;

      try {
        if (action === "read") {
          const response = await washboxApi.markInboxNotificationRead(notificationId);
          if (epoch !== operationEpoch) return;
          dataRevision += 1;
          applyReadResponse(response);
        } else {
          const response = await washboxApi.dismissInboxNotification(notificationId);
          if (epoch !== operationEpoch) return;
          dataRevision += 1;
          applyDismissResponse(response);
        }
      } catch (error: unknown) {
        if (epoch === operationEpoch) {
          setItemError(notificationId, describeApiError(error));
        }
        throw error;
      }
    });
    const request = operation.finally(() => {
      const scheduledIndex = scheduledActions.findIndex(({ token }) => token === actionToken);
      if (scheduledIndex >= 0) scheduledActions.splice(scheduledIndex, 1);
      if (epoch === operationEpoch) syncPendingAction(notificationId);
    });
    scheduledActions.push({
      token: actionToken,
      notificationId,
      type: action,
      epoch: operationEpoch,
      request,
    });
    syncPendingAction(notificationId);
    mutationQueue = request.then(
      () => undefined,
      () => undefined,
    );
    return request;
  }

  function markRead(notificationId: string): Promise<void> {
    const notification = notifications.value.find(({ id }) => id === notificationId);
    if (notification === undefined || notification.readAt !== null) return Promise.resolve();
    return runAction(notificationId, "read");
  }

  function dismiss(notificationId: string): Promise<void> {
    if (!notifications.value.some(({ id }) => id === notificationId)) return Promise.resolve();
    return runAction(notificationId, "dismiss");
  }

  function pendingAction(notificationId: string): NotificationInboxAction | null {
    return pendingActions.value[notificationId] ?? null;
  }

  function itemError(notificationId: string): string | null {
    return itemErrors.value[notificationId] ?? null;
  }

  function reset(): void {
    epoch += 1;
    listRevision += 1;
    dataRevision = 0;
    status.value = "idle";
    notifications.value = [];
    unreadCount.value = null;
    nextCursor.value = null;
    errorMessage.value = null;
    loadMoreErrorMessage.value = null;
    isRefreshing.value = false;
    isLoadingMore.value = false;
    pendingActions.value = {};
    itemErrors.value = {};
    lastLoadedAt.value = 0;
    firstPageLoading = null;
    nextPageLoading = null;
    mutationQueue = Promise.resolve();
    scheduledActions.splice(0);
    notificationTombstones.clear();
  }

  return {
    status,
    notifications,
    unreadCount,
    nextCursor,
    errorMessage,
    loadMoreErrorMessage,
    isRefreshing,
    isLoadingMore,
    pendingActions,
    itemErrors,
    lastLoadedAt,
    load,
    refreshIfStale,
    loadMore,
    markRead,
    dismiss,
    pendingAction,
    itemError,
    reset,
  };
});
